"use client"

import Link from "next/link"
import { useEffect, useMemo, useRef, useState } from "react"
import { AlertCircle } from "lucide-react"
import {
  buildCorrelationId,
  ERROR_BOUNDARY_EVENTS,
  ERROR_BOUNDARY_PROPERTIES,
  isNotFoundBoundaryVariant,
  trackBoundaryEvent,
} from "@/lib/ui/error-boundary"

type ErrorBoundaryCardProps = {
  error: Error
  reset: () => void
  scope: string
  contextLabel: string
  className?: string
}

const SUPPORT_EMAIL = "support@complyrx.io"

export function ErrorBoundaryCard({
  error,
  reset,
  scope,
  contextLabel,
  className = "",
}: ErrorBoundaryCardProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [copied, setCopied] = useState(false)
  const correlationId = useMemo(() => buildCorrelationId(error), [error])
  const isNotFound = useMemo(() => isNotFoundBoundaryVariant(error), [error])

  useEffect(() => {
    headingRef.current?.focus()
    trackBoundaryEvent(ERROR_BOUNDARY_EVENTS.BOUNDARY_RENDERED, {
      [ERROR_BOUNDARY_PROPERTIES.CORRELATION_ID]: correlationId,
      [ERROR_BOUNDARY_PROPERTIES.BOUNDARY_SCOPE]: scope,
      [ERROR_BOUNDARY_PROPERTIES.IS_NOT_FOUND_VARIANT]: isNotFound,
    })
  }, [correlationId, isNotFound, scope])

  async function copyCorrelationId(): Promise<void> {
    try {
      await navigator.clipboard.writeText(correlationId)
      setCopied(true)
      trackBoundaryEvent(ERROR_BOUNDARY_EVENTS.CORRELATION_COPIED, {
        [ERROR_BOUNDARY_PROPERTIES.CORRELATION_ID]: correlationId,
        [ERROR_BOUNDARY_PROPERTIES.BOUNDARY_SCOPE]: scope,
      })
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const description = isNotFound
    ? "That page isn't here. Go to Overview or use search."
    : `${contextLabel} didn't load. Try again or reopen from the workspace.`

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Error ${correlationId}`)}`

  return (
    <div className={`flex min-h-svh items-center justify-center p-6 ${className}`}>
      <div className="w-full max-w-2xl rounded-2xl border border-ink/10 bg-white/80 p-8 shadow-sm">
        <AlertCircle className="h-8 w-8 text-ink/45" aria-hidden="true" />
        <h1
          ref={headingRef}
          role="alert"
          tabIndex={-1}
          className="mt-4 text-2xl font-semibold text-ink"
        >
          {isNotFound ? "That page isn't here." : `${contextLabel} didn't load.`}
        </h1>
        <p className="mt-2 text-sm text-ink/70">
          {description} If this keeps happening, email{" "}
          <a className="underline" href={mailtoHref}>
            {SUPPORT_EMAIL}
          </a>{" "}
          with code {correlationId}.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <code className="rounded bg-ink/5 px-2 py-1 text-xs">{correlationId}</code>
          <button
            type="button"
            onClick={copyCorrelationId}
            className="text-xs underline underline-offset-2"
          >
            {copied ? "Copied" : "Copy code"}
          </button>
        </div>
        <div className="mt-6 flex items-center gap-4">
          <button
            type="button"
            onClick={reset}
            className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-white"
          >
            Try again
          </button>
          <Link href="/tools/trial-tracker" className="text-sm underline underline-offset-2">
            Back to the tracker
          </Link>
        </div>
      </div>
    </div>
  )
}
